import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {MonthlyReportForClinicDgSearchResults} from "../models/datagrid-search-results/monthly-report-for-clinic.dg-search-results.model";
import {MonthlyReportForClinic} from "../models/monthly-report-for-clinic.model";

/**
 * Serwis warstwy danych obsługujący generowanie i dystrybucję raportów rozliczeniowych
 * na poziomie pojedynczej placówki. Zarządza zestawieniami finansowymi,
 * zliczaniem aktywnych użytkowników oraz wysyłką faktur.
 */
@Injectable({
  providedIn: 'root'
})
export class MonthlyReportForClinicService implements DataGridServiceInterface<Observable<MonthlyReportForClinicDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę miesięcznych raportów rozliczeniowych dla konkretnej kliniki.
   * Wykorzystywane do renderowania tabeli (DataGrid) z historią billingów.
   *
   * @param limit Rozmiar strony (maksymalna liczba rekordów).
   * @param offset Przesunięcie względem początku zbioru danych.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny, według której następuje sortowanie.
   * @param showDeletedObjects Flaga określająca widoczność usuniętych raportów.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<MonthlyReportForClinicDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new MonthlyReportForClinicDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new MonthlyReportForClinic(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje miesięczne raporty kliniki na podstawie zdefiniowanych filtrów tekstowych,
   * z zachowaniem mechanizmów paginacji.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca pary klucz-wartość (kolumna -> wyszukiwana fraza).
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga określająca widoczność usuniętych raportów.
   * @returns Observable emitujący przefiltrowany obiekt wyników dla tabeli.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<MonthlyReportForClinicDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/monthly-reports/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new MonthlyReportForClinicDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new MonthlyReportForClinic(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza przeliczenie i wygenerowanie raportu rozliczeniowego dla danej kliniki
   * za określony miesiąc.
   *
   * @param uuid Identyfikator kliniki.
   * @param month Miesiąc rozliczeniowy.
   * @param year Rok rozliczeniowy.
   * @returns Observable emitujący wygenerowany obiekt raportu z zaktualizowanymi sumami.
   */
  prepareReport(uuid: string, month: string, year: string): Observable<MonthlyReportForClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports/${year}/${month}/process/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new MonthlyReportForClinic(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Agreguje logi aktywności użytkowników danej kliniki w danym miesiącu.
   *
   * @param uuid Identyfikator kliniki.
   * @param month Miesiąc rozliczeniowy.
   * @param year Rok rozliczeniowy.
   * @returns Observable potwierdzający poprawne przetworzenie logów.
   */
  prepareUsersActions(uuid: string, month: string, year: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-actions/${year}/${month}/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return "Ok";
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera fizyczną liczbę zidentyfikowanych aktywnych użytkowników w danym cyklu rozliczeniowym.
   *
   * @param uuid Identyfikator kliniki.
   * @param month Miesiąc rozliczeniowy.
   * @param year Rok rozliczeniowy.
   * @returns Observable emitujący całkowitą liczbę aktywnych użytkowników.
   */
  checkActiveUsers(uuid: string, month: string, year: string): Observable<number> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-actions/check/${year}/${month}/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.total;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces wysyłki wygenerowanej faktury przypisanej do wskazanego raportu miesięcznego.
   *
   * @param uuid Identyfikator raportu miesięcznego.
   * @returns Observable emitujący zaktualizowany obiekt raportu (np. ze zmienionym statusem wysyłki).
   */
  sendInvoice(uuid: string): Observable<MonthlyReportForClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-report/new/invoice/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new MonthlyReportForClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
